import { Directive, forwardRef, Input, OnChanges, OnDestroy, OnInit, Provider, SimpleChanges, Type, ViewChild } from '@angular/core';
import {
  AbstractControl,
  AsyncValidator,
  ControlValueAccessor,
  NG_ASYNC_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors
} from '@angular/forms';
import { MonacoEditorChangeTouchDelegate, MonacoEditorComponent } from '../monaco-editor/monaco-editor.component';
import { Observable, of, Subscription, timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { v4 } from 'uuid';
import { Context } from '../context';
import { MonacoEditorLoaderService } from '../monaco-editor/monaco-editor-loader.service';

export function createProviders(type: Type<CodeEditorComponent>): Provider[] {
  return [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => type),
      multi: true
    },
    {
      provide: NG_ASYNC_VALIDATORS,
      useExisting: type,
      multi: true
    }
  ];
}

@Directive({
  standalone: false
})
// tslint:disable-next-line:directive-class-suffix
export abstract class CodeEditorComponent implements ControlValueAccessor, AsyncValidator, OnInit, OnChanges, OnDestroy {
  protected static lastFocusedEditorId = null;

  @ViewChild(MonacoEditorComponent, { static: true }) monacoComponent: MonacoEditorComponent;
  @Input() context: Context;

  protected id = v4();
  protected editor: monaco.editor.IStandaloneCodeEditor;
  protected _monacoLoadedSubscription: Subscription;

  editorOptions: monaco.editor.IStandaloneEditorConstructionOptions;
  onValidate: (control: AbstractControl) => ValidationErrors | null = null;

  abstract createEditorOptions(): monaco.editor.IStandaloneEditorConstructionOptions;
  abstract onDidFocusEditor(): void;

  constructor(
    private monacoEditorLoaderService: MonacoEditorLoaderService) {
  }

  ngOnInit(): void {
    this._monacoLoadedSubscription = this.monacoEditorLoaderService.loaded.subscribe(loaded => {
      if (loaded) {
        this.editorOptions = this.createEditorOptions();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes.context &&
      !changes.context.isFirstChange() &&
      this.editor &&
      CodeEditorComponent.lastFocusedEditorId === this.id) {
      this.setContext();
    }
  }

  ngOnDestroy() {
    if (this.editorOptions.model) {
      this.editorOptions.model.dispose();
    }
    if (this._monacoLoadedSubscription) {
      this._monacoLoadedSubscription.unsubscribe();
    }
  }

  onEditorInit(editor: monaco.editor.IStandaloneCodeEditor) {
    this.editor = editor;

    this.addRemoveActionsAndCommandsNoop();

    // NOTE: we need a strategy for dealing with global settings (TypescriptDefaults) since we have multiple
    // editors visible at the same time and wanting to set different global settings. The current strategy is
    // that we set/reset globals only when focusing, meaning we reset and clean up everything that was previously
    // set (by another editor) when focus is recieved. Also we take care of the other blurred models.
    // As a side effect the last focused editor globals are not cleaned after it looses focus to something
    // that is not an editor. Previously we were doing this when focus was lost (on blur) which didn't work well ->
    // when resetting things on blur, some functionalities of the editor stop working, beware!
    this.editor.onDidFocusEditorText(() => {
      if (CodeEditorComponent.lastFocusedEditorId !== this.id) {
        this.setFakeTypeScriptToAllModels();
        this.setTypescriptToCurrentModel();
        this.resetTypescriptDefaults();
        this.setContext();
      }
      CodeEditorComponent.lastFocusedEditorId = this.id;

      this.onDidFocusEditor();
    });
  }

  setContext() {
    // Explicitly disposing extraLibs models, see
    // https://github.com/microsoft/monaco-editor/issues/2813
    this.disposeExtraLibsModels();

    if (this.context?.text) {
      // For extraLibs information, see those:
      // https://github.com/microsoft/monaco-editor/issues/1415
      // https://github.com/microsoft/monaco-editor/issues/667
      // https://www.typescriptlang.org/docs/handbook/declaration-files/templates/global-d-ts.html
      // https://www.typescriptlang.org/docs/handbook/declaration-files/templates/global-modifying-module-d-ts.html
      // https://stackoverflow.com/a/56984941 (If your file hasn't any import or export line, this
      // file would be executed in global scope that all declaration in it are visible outside this file.)
      monaco.languages.typescript.typescriptDefaults.setExtraLibs([
        { content: this.context.text }
      ]);
    } else {
      monaco.languages.typescript.typescriptDefaults.setExtraLibs([]);
    }
  }

  resetTypescriptDefaults() {
    // reset globals/shared settings
    monaco.languages.typescript.typescriptDefaults.setExtraLibs([]);
    monaco.languages.typescript.typescriptDefaults.setDiagnosticsOptions({});
  }

  setTypescriptToCurrentModel() {
    monaco.editor.setModelLanguage(this.editor.getModel(), 'typescript');
  }

  setFakeTypeScriptToAllModels() {
    const models = monaco.editor.getModels();
    models.forEach(model => {
      if (model.getLanguageId() === 'typescript' && model.uri.toString().startsWith('file:///main_')) {
        monaco.editor.setModelLanguage(model, 'fakeTypescript');
      }
    });
  }

  disposeExtraLibsModels() {
    const models = monaco.editor.getModels();
    models.forEach(model => {
      if (model.uri.toString().startsWith('ts:extralib')) {
        model.dispose();
      }
    });
  }

  // AsyncValidator
  validate(control: AbstractControl): Observable<ValidationErrors | null> {
    if (this.onValidate) {
      // Debounce validation by 500ms. Angular unsubscribes on value change,
      // which cancels the pending timer and restarts the delay.
      return timer(500).pipe(switchMap((v, i) => {
        return of(this.onValidate(control))
      }));
    } else {
      return of(null);
    }
  }

  get value(): string {
    return this.editor.getValue();
  }

  // ControlValueAccessor
  writeValue(value: string): void {
    this.monacoComponent.writeValue(value);
  }

  setDisabledState?(isDisabled: boolean): void {
    this.monacoComponent.setDisabledState(isDisabled);
  }

  registerOnChange(fn: MonacoEditorChangeTouchDelegate): void {
    this.monacoComponent.registerOnChange(fn);
  }

  registerOnTouched(fn: MonacoEditorChangeTouchDelegate): void {
    this.monacoComponent.registerOnTouched(fn);
  }

  addRemoveActionsAndCommandsNoop() {
    // if we want to add custom actions or commands per editor, see:
    // https://microsoft.github.io/monaco-editor/playground.html#interacting-with-the-editor-adding-an-action-to-an-editor-instance
    // https://microsoft.github.io/monaco-editor/playground.html#interacting-with-the-editor-adding-a-command-to-an-editor-instance


    // if we want to remove actions or commands per editor, we can do it in the following hacky way:
    /*
    // the following example removes the `Rename Symbol` action from the Context Menu and its keybinding (F2)
    // command
    // See
    // https://github.com/microsoft/monaco-editor/issues/1280
    // https://github.com/microsoft/monaco-editor/issues/1567
    // https://github.com/microsoft/vscode/blob/main/src/vs/editor/contrib/contextmenu/browser/contextmenu.ts
    // command Ids to remove
    const commandIds = ['editor.action.rename'];
    const contextMenu = this.editor.getContribution('editor.contrib.contextmenu');
    const originalMethod = (contextMenu as any)._getMenuActions as () => any[];
    // take over the _getMenuActions and filter
    (contextMenu as any)._getMenuActions = function () { // tslint:disable-line: only-arrow-functions space-before-function-paren
      const originalActions = originalMethod.apply(contextMenu, arguments);
      const actions = originalActions.filter(a => !commandIds.includes(a.id));
      return actions;
    };
    // override the keybinding with a noop function
    this.editor.addCommand(monaco.KeyCode.F2, () => { });
    // OR reverse engineer the `addCommand` method and do the opposite, see:
    // https://github.com/microsoft/vscode/blob/main/src/vs/editor/standalone/browser/standaloneCodeEditor.ts
    */
  }
}
